import { Icon } from '@iconify/react'
import { reactive, Show, useObservable } from '@legendapp/state/react'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { isEmpty } from 'radash'
import { FC, isValidElement, ReactNode, useCallback, useMemo } from 'react'

import { ConsoleLayout, Tooltip, TooltipContent, TooltipTrigger } from '~in-app/components'
import {
  menuItemIconAnimationVariants,
  menuItemLabelAnimationVariants,
} from '~in-app/components/layout/console/sidebar/animation'
import { cn } from '~in-app/lib/utils'

interface MenuItemProps {
  label: ReactNode | string
  icon?: ReactNode | string
  hasParent?: boolean
  path?: string
  // path?: FileRoutesByPath[keyof FileRoutesByPath]['path']
}

const ReactiveTooltip = reactive(Tooltip)

const MenuItem: FC<MenuItemProps> = ({ icon, label, path, hasParent }) => {
  const layoutContext = ConsoleLayout.useLayoutContext()
  const state$ = useObservable({ isTooltipOpen: false })
  const hasPath = useMemo(() => {
    return !isEmpty(path)
  }, [path])

  const handleTooltipOnOpenChange = useCallback((open: boolean) => {
    const isSidebarOpen = layoutContext.state$.isSidebarOpen.get()

    if (!isSidebarOpen) {
      state$.isTooltipOpen.set(open)
    }
  }, [])

  return (
    <ReactiveTooltip
      $open={() => layoutContext.state$.isSidebarOpen.get() ? false : state$.isTooltipOpen.get()}
      onOpenChange={handleTooltipOnOpenChange}
    >
      <TooltipTrigger asChild>
        <li className="w-full">
          <Link
            disabled={!hasPath}
            to={path}
          >
            {
              ({ isActive }) => {
                const isExactActive = isActive && hasPath

                return (
                  <div className={
                    cn('flex items-center rounded-md px-4 py-2 text-muted-foreground hover:bg-muted', {
                      'bg-muted text-foreground': isExactActive && hasParent,
                      'bg-primary/5 dark:text-primary-400 text-primary-600 dark:bg-primary/20 dark:hover:bg-primary/30 hover:bg-primary/10': isExactActive && !hasParent,
                    })
                  }
                  >
                    <motion.div
                      custom={{ isSidebarOpen: layoutContext.state$.isSidebarOpen.get() }}
                      variants={menuItemIconAnimationVariants}
                    >
                      <Show
                        if={isValidElement(icon)}
                        else={
                          (
                            <Show
                              if={typeof icon === 'string'}
                              else={
                                (
                                  <Icon
                                    height="18"
                                    icon="lucide:dot"
                                    width="18"
                                  />
                                )
                              }
                            >
                              {
                                () => (
                                  <Icon
                                    height="18"
                                    icon={icon as string}
                                    width="18"
                                  />
                                )
                              }
                            </Show>
                          )
                        }
                      >
                        {() => icon}
                      </Show>
                    </motion.div>

                    <motion.div
                      className="flex w-full items-center overflow-hidden"
                      custom={{ isSidebarOpen: layoutContext.state$.isSidebarOpen.get() }}
                      variants={menuItemLabelAnimationVariants}
                    >
                      <p className={
                        cn('min-w-0 flex-1 truncate text-sm font-normal select-none capitalize')
                      }
                      >
                        <Show
                          else={(label as string)}
                          if={isValidElement(label)}
                        >
                          {() => label}
                        </Show>
                      </p>
                    </motion.div>
                  </div>
                )
              }
            }
          </Link>
        </li>
      </TooltipTrigger>

      <TooltipContent
        side="right"
        sideOffset={15}
      >
        <span className="text-xs font-medium capitalize">
          {label}
        </span>
      </TooltipContent>
    </ReactiveTooltip>
  )
}
MenuItem.displayName = 'ConsoleMenuItem'

export { MenuItem }
